import React from 'react';
import { MobileVerifyDataType } from '../context/MobileVerifyContext';
import CustomerHelper from '../helpers/CustomerHelper';

const customerHelper = new CustomerHelper();

interface SavePhoneNumberProps {
  mobileNumber: string;
  receiptPhone?: string;
  receiptCode?: string;
  customerId: string;
  setMobileVerifyData: React.Dispatch<React.SetStateAction<MobileVerifyDataType>>;
}

export interface SavePhoneNumberResponse {
  ok?: boolean;
  response?: MobileVerifyDataType;
  error?: {
    heading: string;
    content?: string;
  };
}

const ERROR_BAD_ARGUMENT =
  window.Jbhifi.storeLocation === 'NZ'
    ? 'Phone number invalid. New Zealand mobile numbers only starting with 02.'
    : 'Phone number invalid. Australian mobile numbers only starting with 04.';

export const savePhoneNumber = async ({
  mobileNumber,
  customerId,
  receiptCode,
  receiptPhone,
  setMobileVerifyData,
}: SavePhoneNumberProps): Promise<SavePhoneNumberResponse> => {
  const handleError = ({ error: { heading, content = '' } }: SavePhoneNumberResponse): SavePhoneNumberResponse => {
    window.scrollTo(0, 0);
    return { ok: false, error: { heading, content } };
  };

  const handleSuccess = (response: MobileVerifyDataType): SavePhoneNumberResponse => {
    return { ok: true, response };
  };

  if ((!mobileNumber && !receiptCode) || !customerId)
    return handleError({ error: { heading: 'Mobile number invalid, give it another go.' } });

  try {
    const response = receiptCode
      ? await customerHelper.sendCustomerMobileVerification(customerId, receiptCode)
      : await customerHelper.setCustomerMobile(customerId, mobileNumber);

    setMobileVerifyData({ ...response, mobileNumber: mobileNumber || receiptPhone, customerId });

    if ((response.isSuccess && !response.isAlreadyVerified && !response.isBadArgument) || response.isAmbiguous) {
      return handleSuccess({ ...response, mobileNumber: mobileNumber || receiptPhone, customerId });
    } else {
      return handleError({
        error: {
          heading: response.isAlreadyVerified
            ? "You've already verified that mobile number."
            : response.isBadArgument
            ? ERROR_BAD_ARGUMENT
            : response.raw?.error?.message ?? "That mobile number doesn't look right...",
          content: response.isAlreadyVerified ? 'Check your details and give it another go.' : undefined,
        },
      });
    }
  } catch (error) {
    return handleError({
      error: {
        heading: 'There was a problem verifying your number. Please try again.',
      },
    });
  }
};
